import Axios from "axios"
import store from "../";

export let apiTimeout = 60000; // milliseconds
export const url      = '/api/'
export const api      = Axios.create({
  baseURL: url,
  timeout: apiTimeout,
  headers: {},
  xsrfCookieName: 'CF-XSRF-TOKEN',
  xsrfHeaderName: 'CF-XSRF-TOKEN',
})

export let routes = {
  fetchData: 'data', // get
  fetchPriorityData: 'priority-data',
  admin: {
    fetch: 'admin', // get
    fetchAll: 'admin/all', // get
    create: 'admin/create', // post
    update: 'admin/update', // post
    delete: 'admin/delete', // post
    logout: 'admin/logout', // post
  },
  catalog: {
    print: 'catalog/print', // post
  },
  orders: {
    refresh: 'orders/refresh', // get
    loadNew: 'orders/load-new', // get
    load: 'orders/load', // get
    loadSingle: 'orders/load-one', // get
    update: 'orders/update', // get
    delete: 'orders/delete', // get
    archive: 'orders/archive', // post
    unarchive: 'orders/unarchive', // post
    print: 'orders/print', // post
    unprint: 'orders/unprint', // post
    void: 'orders/void', // post
    unvoid: 'orders/unvoid', // post
    updateAccountNumber: 'orders/updateAccountNumber', // post
  },
  users: {
    loadPage: 'users/loadPage', // get
    loadSearch: 'users/search', // get
    loadAll: 'users/loadAll', // get
    create: 'users/create', // post
    update: 'users/update', // post
    delete: 'users/delete', // post
    load: 'users/load', // post
    setPrimaryRecipient: 'users/setPrimaryRecipient', // post
    createRecipient: 'users/recipients/create', // post
    updateRecipient: 'users/recipients/update', // post
    deleteRecipient: 'users/recipients/delete', // post
  },
  months: {
    update: 'months/update', // post
  },
  referral_sources: {
    create: 'referral_sources/create', // post
    update: 'referral_sources/update', // post
    delete: 'referral_sources/delete', // post
  },
  pages: {
    fetch: 'pages', // get
    create: 'pages/create', // post
    update: 'pages/update', // post
    sort: 'pages/sort', // post
    delete: 'pages/delete', // post
    createContent: 'pages/create-content', // post
    updateContent: 'pages/update-content', // post
    sortContent: 'pages/sort-content', // post
    deleteContent: 'pages/delete-content', // post
  },
  images: {
    checkName: '/images/check-name',
    upload: '/images/upload',
    updateImageables: '/images/update-imageables',
    update: '/images/update',
    delete: '/images/delete',
  },
  discounts: {
    create: '/discounts/create',
    update: '/discounts/update',
    delete: '/discounts/delete',
  },
  products: {
    createProduct: 'products/create',
    updateProduct: 'products/update',
    updateProductPricing: 'products/pricing/update/',
    updateProductListed: 'products/listed/update/',
    deleteProduct: 'products/delete',
    createBundle: 'products/bundles/create',
    updateBundle: 'products/bundles/update',
    deleteBundle: 'products/bundles/delete',
    createCategory: 'products/categories/create',
    updateCategory: 'products/categories/update',
    deleteCategory: 'products/categories/delete',
    createItem: 'products/items/create',
    updateItem: 'products/items/update',
    deleteItem: 'products/items/delete',
    createTag: 'products/tags/create',
    updateTag: 'products/tags/update',
    deleteTag: 'products/tags/delete',
    createProductBundle: 'products/product-bundles/create',
    updateProductBundle: 'products/product-bundles/update',
    deleteProductBundle: 'products/product-bundles/delete',
    createProductCategory: 'products/product-categories/create',
    updateProductCategory: 'products/product-categories/update',
    deleteProductCategory: 'products/product-categories/delete',
    createProductItem: 'products/product-items/create',
    updateProductItem: 'products/product-items/update',
    deleteProductItem: 'products/product-items/delete',
    createProductTag: 'products/product-tags/create',
    updateProductTag: 'products/product-tags/update',
    deleteProductTag: 'products/product-tags/delete',
    createProductPart: 'products/product-parts/create',
    updateProductPart: 'products/product-parts/update',
    deleteProductPart: 'products/product-parts/delete',
  },
  reports: {
    getProductSoldCounts: 'reports/products', // post
    getEmailList: 'reports/emails', // post
  },
}

// add a response interceptor for redirection on things like authentication
api.interceptors.response.use(undefined, error => {
  if (error.response) {
    // The request was made, but the server responded with a status code that falls out of the range of 2xx
    if (error.response.status === 401) {
      window.location.reload()
    }
    else if (error.response.status === 403) {
      if (window) store.commit('SET_API_ERROR', 'csrf')

      return Promise.reject()
    }
    else if (error.response.status === 422) {
      return Promise.reject(error.response.data)
    }
    else {
      console.log('API Error - status: ' + error.response.status)
      if (window) store.commit('SET_API_ERROR', error.response.status)
    }

  }
  else {
    // Something happened in setting up the request that triggered an Error
    console.log('Error', error);
    if (window) store.commit('SET_API_ERROR', error)
  }

  // do something with the unhandled response error
  return Promise.reject(error);
});

export function request(commit, route, data = null, method = 'get', useCache = true, showload = true) {
  // access the requested HTTP method to be executed
  let execute = methods[method.toLowerCase()]

  // reject for an invalid method
  if (typeof execute !== 'function') {
    return new Promise((resolve, reject) => {
      console.log('Invalid HTTP method');
      return reject()
    });
  }

  // enables or disables response caching (default: enabled)
  // for the 'GET' method only, responses are cached on the originating machine (server or browser)
  // to disable caching for a GET request, pass a false boolean as the 'data' argument
  if (method === 'get' && data === null) data = true

  //console.log('api ' + method.toUpperCase() + ' to ' + route + ((method === 'get' && data) ? ' (cache requested)' : ''))

  return new Promise((resolve, reject) => {
    // set the API_BUSY flag to true, indicating a request is in progress
    if (showload) commit('SET_API_BUSY', true)

    // start a timeout to disable the busy flag for requests that take longer than the specified timeout
    let timeout = setTimeout(function () {
      console.log('API timeout.')
      if (showload) commit('SET_API_BUSY', false)
    }, apiTimeout)

    // finally execute the method
    // on success clear the API_ERROR and API_BUSY flag
    // on failure clear only the API_BUSY flag.
    // for both clear the API timeout.
    return execute(route, data, useCache)
      .then(responseData => {
        clearTimeout(timeout)
        if (showload) commit('SET_API_BUSY', false)
        commit('SET_API_ERROR', false)
        return resolve(responseData)
      })
      .catch(errors => {
        clearTimeout(timeout)
        if (showload) commit('SET_API_BUSY', false)
        return reject(errors)
      })
  })
}

export const methods = {
  get(url, data, useCache = true) {
    const cache = api.cachedData
    if (useCache && cache && cache.has(url)) {
      console.log('api cache response')
      return Promise.resolve(cache.get(url))
    }

    return new Promise((resolve, reject) => {
      api.get(url, typeof data === 'object' ? { params: data } : {})
         .then(response => {
           const val = response.data
           // mark the timestamp when this item is cached
           if (val) val.__lastUpdated = Date.now()
           cache && cache.set(url, val)
           resolve(val)
         })
         .catch(error => {
           reject(error)
         })
    })
  },
  post(url, data) {

    return new Promise((resolve, reject) => {
      api.post(url, data)
         .then(response => {
           resolve(response.data)
         })
         .catch(errors => {
           reject(errors)
         })
    })
  }
}



//////////////////
// WEBPACK FOOTER
// ./vue/src/store/api/index.js
// module id = 6
// module chunks = 0